import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const isDev = process.env.REACT_APP_NODE_ENV === "development";
const isProd = !isDev;

// Critical routes for your app structure
const CRITICAL_ROUTE_PATTERNS = [
  // Auth & entry
  /^\/login$/i,
  /^\/$/i,

  // Club area
  /^\/club(?:\/|$)/i,

  // Super-Admin area
  /^\/super-admin\/official-leagues(?:\/|$)/i,
  /^\/super-admin\/clubs(?:\/|$)/i,
  /^\/super-admin\/fields(?:\/|$)/i,
  /^\/super-admin\/ban-reports(?:\/|$)/i,
  /^\/super-admin\/stories(?:\/|$)/i,
];

function isCriticalRoute(name) {
  return !!name && CRITICAL_ROUTE_PATTERNS.some((re) => re.test(name));
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: `playerdexfc-backoffice@${process.env.REACT_APP_VERSION || "0.0.0"}`,
  environment: process.env.REACT_APP_NODE_ENV || "production",

  integrations: [
    // RR v7-specific BrowserTracing integration
    Sentry.reactRouterV7BrowserTracingIntegration({
      // wire up RR hooks for SPA soft navigations
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,

      // if you want to control when to start page-load / navigation transactions:
      // instrumentPageLoad: true,
      // instrumentNavigation: true,
    }),

    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],

  // Errors: keep all
  sampleRate: 1.0,

  // APM: favor critical routes
  tracesSampler: (ctx) => {
    // Honor parent decision if present
    if (ctx?.parentSampled === true) return true;

    // Try multiple sources for a transaction name
    const txName =
      ctx?.name ?? ctx?.transactionContext?.name ?? ctx?.data?.name ?? "";

    // Fall back to the current path if name is unavailable
    const path =
      ctx?.location?.pathname ??
      (typeof window !== "undefined" ? window.location.pathname : "");

    const candidate = String(txName || path || "");

    if (isCriticalRoute(candidate)) return isProd ? 0.3 : 1.0;
    return isProd ? 0.07 : 0.5;
  },

  // CPU Profiling + Session Replay
  profilesSampleRate: isProd ? 0.05 : 1.0,
  replaysOnErrorSampleRate: isProd ? 0.25 : 1.0,
  replaysSessionSampleRate: isProd ? 0.005 : 0.2,

  // Caps & Hygiene
  maxBreadcrumbs: 50,
  maxValueLength: 2000,
  normalizeDepth: 4,
  sendClientReports: true,

  ignoreErrors: [
    /ResizeObserver loop limit exceeded/i,
    /NetworkError/i,
    /NonCriticalError/i,
    /ChunkLoadError/i,
    /Request aborted/i,
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /safari-web-extension/i,
    /moz-extension:\/\//i,
  ],

  beforeSend(event) {
    if (event.level === "info") return null;
    const url = event.request?.url;
    if (url) {
      try {
        const u = new URL(url);
        u.search = "";
        event.request.url = u.toString();
      } catch {}
    }
    return event;
  },

  beforeSendTransaction(tx) {
    const n = tx?.name || "";
    if (/\/health(z)?$/i.test(n)) return null;
    return tx;
  },
});

// Optional tag visible in Discover/Issues/Perf
Sentry.setTags({ app: "playerdex-fc-backoffice" });

export default Sentry;
